import React, { useEffect, useState, useCallback } from 'react';
import { AnimatedLayout, Text } from '@/components';
import Image from 'next/image';

interface IProps {
  slides: { url: string; title: string; body: string }[];
  autoSlide?: boolean;
  reverseSlide?: boolean;
  autoSlideInterval?: number;
}

function Carousel({
  slides,
  autoSlide = false,
  reverseSlide = false,
  autoSlideInterval = 10000,
}: IProps) {
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  const prevSlide = useCallback(() => {
    const isFirstSlide = currentIndex === 0;
    const newIndex = isFirstSlide ? slides.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
  }, [currentIndex, slides.length]);

  const nextSlide = useCallback(() => {
    const isLastSlide = currentIndex === slides.length - 1;
    const newIndex = isLastSlide ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
  }, [currentIndex, slides.length]);

  const goToSlide = useCallback((slideIndex: number) => {
    setCurrentIndex(slideIndex);
  }, []);

  useEffect(() => {
    if (!autoSlide) return;
    const autoSlideFunction = reverseSlide ? prevSlide : nextSlide;
    const timeoutId = setTimeout(autoSlideFunction, autoSlideInterval);
    return () => clearTimeout(timeoutId);
  }, [autoSlide, reverseSlide, autoSlideInterval, prevSlide, nextSlide]);

  return (
    <div className="w-full h-full pb-16 pt-6 px-4 xxs:pt-0 xs:pt-0 sm:pt-0 relative group flex flex-col justify-center items-center gap-[30px]">
      {slides.map((slide, index) =>
        index === currentIndex ? (
          <AnimatedLayout
            key={index}
            uniqueKey={index}
            className="flex flex-col items-center"
          >
            <Image
              alt="carousel"
              src={slide.url}
              width={334}
              height={334}
              className="pt-12 duration-500 object-cover"
            />

            <div className="px-24 xxs:px-0 flex justify-center align-middle flex-col gap-4">
              <div className="flex justify-center">
                <Text
                  value={slide.title}
                  color="accent"
                  size="h3"
                  className="text-left"
                />
              </div>
              <div className="flex justify-center">
                <Text
                  value={slide.body}
                  color="secondary"
                  className="text-left"
                />
              </div>
            </div>

            <div className="flex justify-center p-8">
              {slides.map((_, slideIndex) => (
                <div
                  key={slideIndex}
                  onClick={() => goToSlide(slideIndex)}
                  className="text-2xl cursor-pointer m-1"
                >
                  <div
                    className={`h-2 rounded-full ${
                      slideIndex === currentIndex
                        ? 'w-8 bg-accent'
                        : 'w-4 bg-accent opacity-10'
                    }`}
                  />
                </div>
              ))}
            </div>
          </AnimatedLayout>
        ) : null,
      )}
    </div>
  );
}

export default Carousel;
